import { getEntryInfoFromUrl } from '~/server/utils/uuidHelper';

const isExternalUrl = require('~/server/models/helpers/isExternalUrl');
const isRelativeUrl = require('~/server/models/helpers/isRelativeUrl');

/**
 * returns cetDataVarsObject with the base data attributes for internal and external links on entries
 *
 * @export
 * @param {string} unit unit where the link appears
 * @param {string} link url or slug of the link
 * @param {string} text text that is linked
 * @param {string} type type of item ie: text, button
 * @param {string} unitName name of the unit where the links are
 * @param {object} options receives other optional settings such as: [sidebar=''] If the unit is a sidebar we need to know if its on the left or right sidebar (for example share bar at the left of articles)
 * @return {object} cetDataVars object containing base data attributes
 */
export function setLinkCetDataVars(unit, link, text, type, unitName = '', options = {}) {
  const { sidebar = '' } = options;
  let parsedUrl;
  let isExternalLink = false;
  let contentType;
  const linkIsRelative = isRelativeUrl(link);
  const linkIsEntry = link && link.includes('entry/');
  const linkIsGame = link && link.includes('games/');
  const linksToMembership = link && link.includes('membership');
  let targetContentId = link;

  if (linkIsRelative) {
    if (linksToMembership) {
      contentType = 'utility';
    } else if (linkIsGame) {
      contentType = 'game';
    } else {
      contentType = linkIsEntry ? 'buzz' : 'feed';
    }
  } else {
    try {
      parsedUrl = new URL(link);
    } catch (e) {
      return {};
    }
    isExternalLink = isExternalUrl(parsedUrl);
    if (isExternalLink) {
      contentType = 'url';
    } else if (linkIsEntry) {
      contentType = 'buzz';
      // eslint-disable-next-line prefer-destructuring
      targetContentId = getEntryInfoFromUrl(link)[1];
    } else if (linksToMembership) {
      targetContentId = parsedUrl.pathname;
      contentType = 'utility';
    } else {
      targetContentId = parsedUrl.pathname;
      contentType = 'feed';
    }
  }
  const cetDataVars = {
    'data-vars-item-name': text,
    'data-vars-item-type': type,
    'data-vars-unit-name': sidebar || unitName,
    'data-vars-unit-type': unit,
    'data-vars-target-content-id': targetContentId,
    'data-vars-target-content-type': contentType,
    'data-vars-type': isExternalLink ? 'web_external_link' : 'web_internal_link',
  };

  return cetDataVars;
}

function mapModulePositionToUnit(position, entryId = '') {
  const rightSidebar = { unitName: 'right', unitType: 'sidebar' };
  const leftSidebar = { unitName: 'left', unitType: 'sidebar' };
  const buzzBody = { unitName: entryId, unitType: 'buzz_body' };
  const buzzHead = { unitName: entryId, unitType: 'buzz_head' };
  const navBar = { unitName: entryId, unitType: 'nav_bar' };
  const feed = { unitName: 'main', unitType: 'feed' };
  const mainNavBar = { unitName: 'site_header', unitType: 'nav_bar' };

  if (entryId) {
    if (position === 'header') {
      return buzzHead;
    } if (position === 'footer') {
      return buzzBody;
    } if (position === 'side') {
      return leftSidebar;
    } if (position === 'follow' || position === 'sidebar') {
      return rightSidebar;
    } if (position === 'liveblog' || position === 'liveblog_copy_link') {
      return buzzBody;
    } if (position === 'nav_bar') {
      return navBar;
    }
  } else if (position === 'follow' || position === 'sidebar') {
    return rightSidebar;
  } else if (position === 'nav_bar') {
    return mainNavBar;
  }
  return feed;
}

/**
 *
 *
 * @export
 * @param {string} position position of the item/unit that generates the action in order to map it to its cet value
 * @param {string} itemName name of the item that does the action, ie: fecebook, comments, bookmarks
 * @param {string} [entryId=''] entryId of where the action takes place
 * @return {object} cetDataVars Object with base data
 */
export function setWebActionCetDataVars(position, itemName, entryId = '') {
  if (!position) {
    return {};
  }
  const unitVars = mapModulePositionToUnit(position, entryId);

  let actionType = 'share';
  if (position === 'follow') {
    actionType = 'subscribe_follow';
  } else if (itemName === 'bookmark' || position === 'liveblog_copy_link') {
    actionType = 'save';
  } else if (['comments', 'open_notification_center'].includes(itemName)) {
    actionType = 'open';
  } else if (itemName === 'see_more' && position !== 'liveblog') {
    actionType = 'navigate';
  } else if (itemName === 'more_updates' || (itemName === 'see_more' && position === 'liveblog')) {
    actionType = 'show';
  }

  const cetDataVars = {
    'data-vars-item-name': itemName,
    'data-vars-item-type': 'button',
    'data-vars-action-type': actionType,
    'data-vars-action-value': itemName,
    'data-vars-unit-type': unitVars.unitType,
    'data-vars-unit-name': unitVars.unitName,
    'data-vars-type': 'web_content_action',
  };
  return cetDataVars;
}

export function getRecircCetDataVars(unitName, unitType, subunitName, subunitType) {
  return {
    'data-vars-unit-name': unitName,
    'data-vars-unit-type': unitType,
    'data-vars-subunit-name': subunitName,
    'data-vars-subunit-type': subunitType,
  };
}

export function setZoneLevelCetDataVars(zone) {
  return {
    'data-vars-subunit-name': zone,
    'data-vars-subunit-type': 'package',
    'data-vars-unit-name': 'main',
    'data-vars-unit-type': 'feed',
  };
}

export function setSubbuzzCetDataVars(unitName, unitType, subUnitName, subUnitType, itemName) {
  return {
    'data-vars-subunit-name': subUnitName,
    'data-vars-subunit-type': subUnitType,
    'data-vars-unit-name': unitName,
    'data-vars-unit-type': unitType,
    'data-vars-item-name': itemName,
    'data-vars-item-type': 'subbuzz',
  };
}
